import React, { useCallback, useEffect } from 'react'
import { Offcanvas } from 'react-bootstrap';
import { toast } from 'sonner';
import { useRoleById } from '../hooks/useRoleById';
import { usePermissions } from '../hooks/usePermissions';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
import { SyncPermissionsFormData, SyncPermissionsSchema } from '../schema/SyncPermissionsSchema';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAssignPermissions } from '../hooks/useAssignPermissions';
import { useQueryClient } from '@tanstack/react-query';

interface AssignPermissionDialogProps {
    show: boolean;
    onClose: () => void;
    roleId: number | undefined;
}

const AssignPermissionDialog: React.FC<AssignPermissionDialogProps> = ({ show, onClose, roleId }) => {
    const { data: role, isFetching, isError, isSuccess, error } = useRoleById({
        id: roleId
    });
    const { data: permissions, isFetching: isPermissionsFetching, isError: isPermissionsError, isSuccess: isPermissionsSuccess, error: permissionsError } = usePermissions({
        roleId: roleId
    });

    if (isError || isPermissionsError) {
        toast.error(error?.message || permissionsError?.message || 'Failed to fetch role', {
            id: 'fetch-role',
        });
        onClose();
        return;
    }

    const { handleSubmit, setValue, watch, formState: { errors }, setError, reset } = useForm<SyncPermissionsFormData>({
        resolver: zodResolver(SyncPermissionsSchema),
        defaultValues: {
            permissions: [],
        },
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    useEffect(() => {
        if (role) {
            reset({
                permissions: role.data.permissions.map(permission => permission.name),
            });
        }
    }, [role, reset]);

    const selectedPermissions = watch("permissions");

    const { mutate, isPending } = useAssignPermissions({ roleId });

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: SyncPermissionsFormData) => {
        toast.loading('Assign Permissions to Role...', {
            id: 'assign-permissions-to-role',
        });
        mutate(data, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['roles'].includes(key as string))
                });

                toast.success('Permissions role assigned successfully', {
                    id: 'assign-permissions-to-role',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof SyncPermissionsFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to permissions assign role', { id: 'assign-role' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to permissions assign role', { id: 'assign-permissions-to-role' });
                }
            }
        });
    }, [mutate, roleId]);

    const handlePermissionToggle = (permissionName: string, isChecked: boolean) => {
        setValue(
            "permissions",
            isChecked
                ? [...selectedPermissions, permissionName] // Add permission
                : selectedPermissions.filter((name: string) => name !== permissionName) // Remove permission
        );
    };

    const handleGroupToggle = (groupPermissions: any[], isChecked: boolean) => {
        const groupPermissionNames = groupPermissions.map((permission) => permission.name);

        setValue(
            "permissions",
            isChecked
                ? Array.from(new Set([...selectedPermissions, ...groupPermissionNames])) // Add all group permissions
                : selectedPermissions.filter((name) => !groupPermissionNames.includes(name)) // Remove all group permissions
        );
    };

    return (
        <Offcanvas show={show} onHide={onClose} placement="end" style={{ width: '45%' }} >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Assign Permission</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {isError || isPermissionsError && <FormError />}
                {isFetching || isPermissionsFetching && <FormSpinner />}
                {isSuccess && isPermissionsSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                        {/* Role and Permissions Summary */}
                        <div className="mb-4 p-3 bg-light rounded shadow-sm">
                            <h4 className="mb-3 text-primary d-flex align-items-center">
                                <span className="me-2">Role:</span>
                                <span className="badge bg-primary text-uppercase text-white">{role?.data.name}</span>
                            </h4>
                            <div className="border-top pt-3">
                                <h5 className="mb-3 text-secondary d-flex align-items-center">
                                    <span className="me-2">Permissions</span>
                                    <span className="badge bg-info text-white">{Object.keys(permissions.data).length} Groups</span>
                                </h5>
                                <p className="text-muted mb-0">Select or manage permissions for this role:</p>
                            </div>
                        </div>

                        {/* Permissions Table */}
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Group</th>
                                        <th>Permission</th>
                                        <th>Select</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(permissions.data).map(([groupName, groupPermissions]) => (
                                        <React.Fragment key={groupName}>
                                            {/* Group Row */}
                                            <tr>
                                                <td rowSpan={groupPermissions.length + 1} className="align-middle">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`group-${groupName}`}
                                                            checked={groupPermissions.every((perm) =>
                                                                selectedPermissions.includes(perm.name)
                                                            )}
                                                            onChange={(e) =>
                                                                handleGroupToggle(groupPermissions, e.target.checked)
                                                            }
                                                        />
                                                        <label htmlFor={`group-${groupName}`} className="form-check-label">
                                                            {groupName || "Ungrouped"}
                                                        </label>
                                                    </div>
                                                </td>
                                            </tr>
                                            {/* Individual Permission Rows */}
                                            {groupPermissions.map((permission) => (
                                                <tr key={permission.id}>
                                                    <td>{permission.display_name || permission.name}</td>
                                                    <td>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`permission-${permission.id}`}
                                                                checked={selectedPermissions.includes(permission.name)}
                                                                onChange={(e) =>
                                                                    handlePermissionToggle(permission.name, e.target.checked)
                                                                }
                                                            />
                                                            <label
                                                                htmlFor={`permission-${permission.id}`}
                                                                className="form-check-label"
                                                            >
                                                                Select
                                                            </label>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Validation Error */}
                        {errors.permissions && (
                            <div className="alert alert-danger mt-3">
                                {errors.permissions.message || "Please select at least one permission."}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button type="submit" className="btn btn-primary mt-3" disabled={isPending}>
                            {isPending ? 'Saving...' : 'Save'}
                        </button>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default AssignPermissionDialog